import { useToast } from '@chakra-ui/react'
import { InvoiceStatus } from '@handlers/sessions/components/invoice-status-badge'
import confetti from 'canvas-confetti'
import React, {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { useQueryClient } from 'react-query'
import {
  getInvoiceControllerFindByIdQueryKey,
  useInvoiceControllerFinalizeInvoice,
  useInvoiceControllerFindById
} from '~/clinician-api'
import type { InvoiceDto } from '~/clinician-api/models'

interface InvoiceManagementContextType {
  isFinalizing: boolean
  isFinalized: boolean
  handleFinalizeSession: () => void
  invoiceId?: string
  invoice?: InvoiceDto
  isInvoiceLoading: boolean
}

interface InvoiceManagementProviderProps {
  children: ReactNode
  invoiceId?: string
}

const InvoiceManagementContext = createContext<
  InvoiceManagementContextType | undefined
>(undefined)

export const InvoiceManagementProvider: FC<InvoiceManagementProviderProps> = ({
  children,
  invoiceId
}) => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [isFinalized, setIsFinalized] = useState(false)

  const { mutateAsync: finalizeInvoice } = useInvoiceControllerFinalizeInvoice({
    mutation: {
      onSuccess: (_data: any) => {
        setIsFinalized(true)

        queryClient.invalidateQueries(
          getInvoiceControllerFindByIdQueryKey(invoiceId!)
        )

        toast({ title: 'Invoice finalized', status: 'success' })

        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        })
      },
      onError: (error: any) => {
        setIsFinalized(false)

        toast({
          title: 'Invoice finalize failed',
          description: 'An error occurred. Please try again.',
          status: 'error'
        })
        console.error(error)
      },
      onSettled: () => {
        setIsFinalizing(false)
      }
    }
  })

  const handleFinalizeSession = useCallback(() => {
    if (!invoiceId) return
    setIsFinalizing(true)
    finalizeInvoice({ invoiceId })
  }, [invoiceId, finalizeInvoice])

  const { data: invoice, isLoading } = useInvoiceControllerFindById(
    invoiceId ?? '',
    {
      query: {
        enabled: !!invoiceId
      }
    }
  )

  // Initialize isFinalized based on invoice status
  useEffect(() => {
    if (invoice) {
      if (invoice.status !== InvoiceStatus.DRAFT) {
        setIsFinalized(true)
      }
    }
  }, [invoice?.status])

  return (
    <InvoiceManagementContext.Provider
      value={{
        isFinalizing,
        isFinalized,
        handleFinalizeSession,
        invoiceId,
        invoice,
        isInvoiceLoading: isLoading
      }}
    >
      {children}
    </InvoiceManagementContext.Provider>
  )
}

export const useInvoiceManagement = (): InvoiceManagementContextType => {
  const context = useContext(InvoiceManagementContext)
  if (context === undefined) {
    throw new Error(
      'useInvoiceManagement must be used within a InvoiceManagementProvider'
    )
  }
  return context
}
